// The core mechanic, expressed as numbers. Sourced verbatim from the wiki
// (Concept / Features / USPs): three independent earning streams calculated at
// every weigh-in. Shared by the interactive calculator and the phone demo so
// the whole site does the same maths.

export const RATES = {
  /** All-time weight low: 2,000 cal per kg lost below your personal lowest. */
  allTimeLowPerKg: 2000,
  /** Rolling three-day average low: 2,000 cal per kg when the average hits a new best. */
  threeDayAvgPerKg: 2000,
  /** Time elapsed: 500 cal per 24 hours since your last weigh-in. */
  timePer24h: 500,
  /** The science anchor: ~7,700 cal stored in a kilogram of fat. */
  scienceCalPerKg: 7700,
} as const;

export type StreamKey = "allTime" | "threeDay" | "time";

export type Breakdown = {
  allTime: number;
  threeDay: number;
  time: number;
  total: number;
};

/**
 * Estimate a WEEK of banked calories.
 * @param kgLost  kilograms lost across the week (assumed to set a new personal
 *                low, which also drags the 3-day average to a new best).
 * @param daysLogged  how many days the user weighed in (drives the time stream).
 */
export function weeklyEstimate(kgLost: number, daysLogged: number): Breakdown {
  const kg = Math.max(0, kgLost);
  const days = Math.max(0, Math.min(7, daysLogged));
  const allTime = Math.round(kg * RATES.allTimeLowPerKg);
  const threeDay = Math.round(kg * RATES.threeDayAvgPerKg);
  const time = Math.round(days * RATES.timePer24h);
  return { allTime, threeDay, time, total: allTime + threeDay + time };
}

/** Earnings from a SINGLE weigh-in that sets a new low `deltaKg` below your best. */
export function weighInEarnings(deltaKg: number): Breakdown {
  const kg = Math.max(0, deltaKg);
  const allTime = Math.round(kg * RATES.allTimeLowPerKg);
  const threeDay = Math.round(kg * RATES.threeDayAvgPerKg);
  const time = RATES.timePer24h; // one day elapsed
  return { allTime, threeDay, time, total: allTime + threeDay + time };
}

export const STREAMS = [
  {
    key: "allTime" as StreamKey,
    label: "All-time low",
    rate: "2,000 cal / kg",
    blurb:
      "Every kilogram you drop below your personal best pays out 2,000 calories. Scales right down to the gram — 200 g is 400 calories in the bank.",
    example: "Lose 0.5 kg from your low → bank 1,000 cal",
    accent: "teal" as const,
  },
  {
    key: "threeDay" as StreamKey,
    label: "Three-day average",
    rate: "2,000 cal / kg",
    blurb:
      "A second stream rewards your rolling three-day average hitting a new best — so a real downward trend pays out even when a single morning is noisy.",
    example: "New 3-day-average low of 0.5 kg → bank 1,000 cal",
    accent: "violet" as const,
  },
  {
    key: "time" as StreamKey,
    label: "Just for showing up",
    rate: "500 cal / 24h",
    blurb:
      "Weigh in and earn 500 calories every day, whether the number moved or not. Consistency is the habit — and it always pays.",
    example: "Log today → bank 500 cal, streak intact",
    accent: "teal" as const,
  },
];
